import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const AdminProjects = ({ openCreateModal = false }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(openCreateModal);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'discovery',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  // Open modal when openCreateModal prop changes
  useEffect(() => {
    if (openCreateModal) {
      setShowCreateModal(true);
    }
  }, [openCreateModal]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          users!projects_client_id_fkey(email, full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching projects:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name')
        .eq('role', 'client')
        .order('email', { ascending: true });

      if (error) throw error;
      setClients(data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching clients:', error);
      }
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data - convert empty strings to null for optional fields
      const cleanedForm = {
        ...projectForm,
        client_id: projectForm.client_id || null,
        start_date: projectForm.start_date || null,
        end_date: projectForm.end_date || null,
        description: projectForm.description || null
      };

      const { data: newProject, error } = await supabase
        .from('projects')
        .insert([cleanedForm])
        .select()
        .single();

      if (error) throw error;

      // Create default 5 phases for new project
      const defaultPhases = [
        { name: 'Discovery', description: 'Project kickoff and requirements gathering', order_index: 1, status: 'pending' },
        { name: 'UI/UX Design', description: 'Design mockups and user experience', order_index: 2, status: 'pending' },
        { name: 'Development', description: 'Code implementation and feature development', order_index: 3, status: 'pending' },
        { name: 'Testing', description: 'Quality assurance and bug fixes', order_index: 4, status: 'pending' },
        { name: 'Launch', description: 'Deployment and go-live', order_index: 5, status: 'pending' }
      ];

      const phasesWithProjectId = defaultPhases.map(phase => ({
        ...phase,
        project_id: newProject.id
      }));

      const { error: phasesError } = await supabase
        .from('timeline_phases')
        .insert(phasesWithProjectId);

      if (phasesError) {
        console.error('Error creating default phases:', phasesError);
      }

      await fetchProjects();
      setShowCreateModal(false);
      setProjectForm({ name: '', description: '', client_id: '', status: 'discovery', start_date: '', end_date: '' });

      // Navigate to the new project
      navigate(`/admin/project/${newProject.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project: ' + (error.message || 'Unknown error'));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'discovery': 'bg-blue-100 text-blue-800',
      'design': 'bg-purple-100 text-purple-800',
      'development': 'bg-yellow-100 text-yellow-800',
      'testing': 'bg-orange-100 text-orange-800',
      'deployment': 'bg-green-100 text-green-800',
      'completed': 'bg-gray-100 text-gray-800',
      'on-hold': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">All Projects</h1>
            <p className="text-gray-600">Manage all client projects</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
          >
            + New Project
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
          />
          <svg
            className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </motion.div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            onClick={() => navigate(`/admin/project/${project.id}`)}
            className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex-1">{project.name}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                {project.status?.replace('-', ' ').toUpperCase()}
              </span>
            </div>

            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {project.description || 'No description provided'}
            </p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Client:</span>
                <span className="font-semibold text-gray-900">
                  {project.users?.full_name || project.users?.email || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Start Date:</span>
                <span className="font-semibold text-gray-900">{formatDate(project.start_date)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">End Date:</span>
                <span className="font-semibold text-gray-900">{formatDate(project.end_date)}</span>
              </div>
            </div>

            <button className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold text-sm transition-colors">
              View Project →
            </button>
          </motion.div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No projects found</p>
        </div>
      )}

      {/* Create Project Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Create New Project</h3>
              <form onSubmit={handleCreateProject}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project Name *</label>
                    <input
                      type="text"
                      value={projectForm.name}
                      onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter project name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Client *</label>
                    <select
                      value={projectForm.client_id}
                      onChange={(e) => setProjectForm({ ...projectForm, client_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      required
                    >
                      <option value="">Select a client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.full_name || client.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Project description"
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <select
                      value={projectForm.status}
                      onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="discovery">Discovery</option>
                      <option value="design">Design</option>
                      <option value="development">Development</option>
                      <option value="testing">Testing</option>
                      <option value="deployment">Deployment</option>
                      <option value="completed">Completed</option>
                      <option value="on-hold">On Hold</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={projectForm.start_date}
                        onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={projectForm.end_date}
                        onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> This will automatically create 5 default phases: Discovery, UI/UX Design, Development, Testing, and Launch.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    Create Project
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminProjects;
